import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Search, LayoutDashboard, Loader2 } from 'lucide-react';
import { Captcha } from '@/components/ui/captcha';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Command, CommandGroup, CommandItem } from '@/components/ui/command';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { IssueSubmittedDialog } from '@/components/ui/issue-submitted-dialog';
import { TrackIssue } from '@/components/track-issue';
import { supabase, retryableQuery } from '@/lib/supabase';
import { checkForSpam } from '@/lib/openai';
import type { School, Grade } from '@/types';

interface FormData {
  school_id: string;
  grade_id: string;
  teacher_name: string;
  contact_number: string;
  subject: string;
  details: string;
}

const initialFormData: FormData = {
  school_id: '',
  grade_id: '',
  teacher_name: '',
  contact_number: '',
  subject: '',
  details: '',
};

export default function ReportIssue() {
  const navigate = useNavigate();
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [schools, setSchools] = useState<School[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [filteredGrades, setFilteredGrades] = useState<Grade[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [commandOpen, setCommandOpen] = useState(false);
  const [submittedIssueNumber, setSubmittedIssueNumber] = useState('');
  const [showTrackIssue, setShowTrackIssue] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchSchools();
    fetchGrades();
  }, []);

  useEffect(() => {
    if (formData.school_id) {
      const school = schools.find((s) => s.id === formData.school_id);
      if (school) {
        setFilteredGrades(grades.filter((g) => g.school_level_id === school.school_level_id));
      }
    } else {
      setFilteredGrades([]);
    }
  }, [formData.school_id, schools, grades]);

  const fetchSchools = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await retryableQuery(() => 
        supabase
          .from('sms_schools')
          .select('*, school_level:sms_school_levels(*)')
          .order('name')
      );
     
      if (error) throw error;
      if (data) {
        setSchools(data);
      }
    } catch (error) {
        console.error('Error fetching schools:', error);
        toast.error('Failed to load schools. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGrades = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await retryableQuery(() =>
        supabase
          .from('sms_grades')
          .select('*')
          .order('name')
      );
     
      if (error) throw error;
      if (data) {
        setGrades(data);
      }
    } catch (error) {
        console.error('Error fetching grades:', error);
        toast.error('Failed to load grades. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCaptchaValid) {
      toast.error('Please complete the CAPTCHA correctly');
      return;
    }
    if (!formData.school_id) {
      toast.error('Please select a school from the list');
      return;
    }
    setIsSubmitting(true);

    try {
      // Check for spam content
      const spamCheck = await checkForSpam({
        teacher_name: formData.teacher_name,
        subject: formData.subject,
        details: formData.details
      });

      const status = spamCheck.isSpam ? 'rejected' : 'new';
      const remarks = spamCheck.isSpam ? `Automatically rejected: ${spamCheck.reason}` : null;

      const { data, error } = await supabase
        .from('sms_reported_issues')
        .insert({
          school_id: formData.school_id,
          grade_id: formData.grade_id,
          teacher_name: formData.teacher_name,
          contact_number: formData.contact_number || null,
          subject: formData.subject,
          details: formData.details,
          status,
          remarks,
        })
        .select()
        .single();

      if (error) throw error;

      setSubmittedIssueNumber(data.issue_number);
      setDialogOpen(true);
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error reporting issue:', error);
      toast.error('Failed to report issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-1">
      <span className="text-sm font-medium">{children}</span>
      <span className="text-red-500">*</span>
    </div>
  );

  if (showTrackIssue) {
    return <TrackIssue onBack={() => setShowTrackIssue(false)} />;
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section with Background */}
      <div 
        className="relative bg-[url('https://images.unsplash.com/photo-1606761568499-6d2451b23c66')] bg-cover bg-center"
        style={{ 
          backgroundImage: `
            linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)),
            url('https://images.unsplash.com/photo-1606761568499-6d2451b23c66')
          `
        }}
      >
        {/* Navigation */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="bg-white hover:bg-gray-100 text-gray-900"
            >
              <LayoutDashboard className="mr-2 h-4 w-4" />
              Go to Dashboard
            </Button>
          </div>
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 py-16 text-center text-white">
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Report a Teacher Not Teaching Issue
          </h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto mb-8">
            Help us maintain high educational standards by reporting concerns professionally and confidentially.
          </p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => {
                const form = document.getElementById('report-form');
                form?.scrollIntoView({ behavior: 'smooth' });
              }}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Submit Report
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="bg-white hover:bg-gray-100 text-gray-900"
              onClick={() => setShowTrackIssue(true)}
            >
              <Search className="mr-2 h-4 w-4" />
              Track Report
            </Button>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="container max-w-2xl mx-auto px-4 py-16" id="report-form">
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <RequiredLabel>Institution Name</RequiredLabel>
                  <div className="relative">
                    <Input
                      placeholder="Type to search for a school..."
                      value={schoolSearch}
                      onChange={(e) => {
                        setSchoolSearch(e.target.value);
                        setCommandOpen(true);
                      }}
                      onClick={() => setCommandOpen(true)}
                      required
                    />
                    {commandOpen && (
                      <div className="absolute w-full z-50 top-full mt-1">
                        <Card>
                          <Command className="rounded-lg border shadow-md">
                            <CommandGroup className="max-h-64 overflow-auto">
                              {schools
                                .filter(school =>
                                  school.name.toLowerCase().includes(schoolSearch.toLowerCase())
                                )
                                .map((school) => (
                                  <CommandItem
                                    key={school.id}
                                    onSelect={() => {
                                      setFormData({
                                        ...formData,
                                        school_id: school.id,
                                        grade_id: '',
                                      });
                                      setSchoolSearch(school.name);
                                      setCommandOpen(false);
                                    }}
                                    className="cursor-pointer"
                                  >
                                    {school.name}
                                  </CommandItem>
                                ))}
                              {schools.filter(school =>
                                school.name.toLowerCase().includes(schoolSearch.toLowerCase())
                              ).length === 0 && (
                                <div className="p-2 text-sm text-muted-foreground">
                                  School not found
                                </div>
                              )}
                            </CommandGroup>
                          </Command>
                        </Card>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <RequiredLabel>Which CLASS is NOT being taught?</RequiredLabel>
                  <Select
                    value={formData.grade_id}
                    onValueChange={(value) => setFormData({ ...formData, grade_id: value })}
                    disabled={!formData.school_id}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select class or grade level" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredGrades.map((grade) => (
                        <SelectItem key={grade.id} value={grade.id}>
                          {grade.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <RequiredLabel>What is the name of the teacher who is NOT teaching?</RequiredLabel>
                  <Input
                    value={formData.teacher_name}
                    onChange={(e) => setFormData({ ...formData, teacher_name: e.target.value })}
                    placeholder="Enter the teacher's complete name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <RequiredLabel>Subject Not Being Taught</RequiredLabel>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="For example: Mathematics, English A, PoB, Literacy, etc."
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Your Contact Number</label>
                  <Input
                    value={formData.contact_number}
                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                    placeholder="This phone number will NOT be shared with anyone. The Ministry of Education will use it to reach out to the complainant to inform him or her of the course of action which will be taken."
                    type="tel"
                  />
                </div>

                <div className="space-y-2">
                  <RequiredLabel>Details of Concern</RequiredLabel>
                  <Textarea
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    placeholder="Please provide specific details about your concern, include relevant information such as dates, times and specific incidents."
                    rows={4}
                    required
                  />
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Important</AlertTitle>
                  <AlertDescription>
                    After submitting, you will receive a unique issue number for tracking your report.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <RequiredLabel>Verify you are human</RequiredLabel>
                  <Captcha onVerify={setIsCaptchaValid} />
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2" 
                    disabled={isSubmitting || !isCaptchaValid}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Report'
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <IssueSubmittedDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        issueNumber={submittedIssueNumber}
      />
    </div>
  );
}